'use strict'

import { $q, $log } from '../helpers/angular-migration-helpers'
import FeatureCheck from '../helpers/featurecheck'
import { createCompressionWorker } from '../helpers/worker'

unsupportedFormats = [
  # Compression formats
  /application\/zip/

  # Multimedia containers
  /application\/mxf/

  # Audio formats
  /audio\/mp3/
  /audio\/mpeg/

  # Image formats
  /image\/x-adobe-dng/
  /image\/jpeg/
  /image\/gif/
  /image\/tiff/

  # All video formats
  /video\/.*/
]

unsupportedExtensions = [
  # Compression formats
  /\.7z$/
  /\.dmg$/
  /\.gz$/
  /\.rar$/
  /\.tgz$/
  /\.zip$/

  # Image formats
  /\.gif$/
  /\.jpg$/
  /\.jpeg$/
  /\.png$/

  # RAW image file formats
  /\.3fr$/ # Hasselblad
  /\.arw$/ # Sony
  /\.cr[w2]$/ # Canon
  /\.dng$/ # Adobe digital negative
  /\.kdc$/ # Kodak
  /\.mrw$/ # Minolta
  /\.nef$/ # Nikon
  /\.orf$/ # Olympus
  /\.pex$/ # Pentax
  /\.ptx$/ # Pentax
  /\.raf$/ # Fuji
  /\.rw2$/ # Panasonic
  /\.srw$/ # Samsung
  /\.x3f$/ # Sigma

  # Audio formats
  /\.flac$/
  /\.mp3$/
  /\.mpa$/
  /\.ogg$/

  # Video formats
  /\.avi$/
  /\.avchd$/
  /\.flv$/
  /\.mkv$/
  /\.mp4$/
  /\.mpe$/
  /\.mpeg$/
  /\.mov$/
  /\.mpg$/
  /\.mpv$/
  /\.mts$/
  /\.m2ts$/
  /\.mv1$/
  /\.mv2$/
  /\.vob$/
  /\.wmv$/

  # Multimedia containers
  /\.mxf$/

  # Office files
  /\.docx$/
  /\.pptx$/
  /\.xlsx$/

  # Windows setup
  /\.msi$/
]

defaultCompressionLevel = 6

class Compression
  constructor: (compressionLevel) ->
    @compressionLevel = compressionLevel || defaultCompressionLevel

  initializePromise: ->
    dfd = $q.defer()
    worker = createCompressionWorker()
    # Assign web worker
    @webWorker = worker
    dfd.resolve()

    # Return the promise
    dfd.promise

  getString: ->
    "compression:level=#{@compressionLevel}"

  preProcessFile: (fileInfo) ->
    # If compression isn't supported, then we won't compress
    return true if !FeatureCheck.canCompress()

    # Zero-length files shouldn't be compressed
    return true if fileInfo.size == 0

    # Check if it is an unsupported file type
    if fileInfo.type
      lowerCaseType = fileInfo.type.toLowerCase()
      for unsupportedFormat in unsupportedFormats
        return true if unsupportedFormat.test lowerCaseType

    # Check if it is an unsupported file extension
    lowerCaseName = fileInfo.name.toLowerCase()
    for unsupportedExtension in unsupportedExtensions
      return true if unsupportedExtension.test lowerCaseName

    # No unsupported file, so compress it
    fileInfo.compressionLevel = @compressionLevel

    # Continue processing
    return true

  processSlice: (fileInfo, uncompressedBlob, chunk) ->
    # Check if we need to compress the slice
    return uncompressedBlob if !fileInfo.compressionLevel

    # Determine when we started processing
    start = Date.now()

    dfd = $q.defer()

    # Add the response handler
    responseHandler = (e) =>
      if e.data.id == fileInfo.id
        # Handle errors
        if e.data.chunk isnt chunk
          $log.error(
            "#{fileInfo.name} (#{chunk + 1}/#{
              fileInfo.totalChunks
            }): Compression expected chunk #{chunk}, but got #{e.data.chunk}."
          )

        # Create the complete blob
        compressedBlob = new Blob e.data.deflatedBlocks

        # Initialize file information during the first chunk
        if chunk == 0
          # Reset compressed size
          fileInfo.tempCompressedSize = 0

        # Keep track of the compressed size
        fileInfo.tempCompressedSize += compressedBlob.size

        # Save CRC during the last blob and save the total compressed size
        if chunk == fileInfo.totalChunks - 1
          fileInfo.crc = e.data.crc if e.data.crc
          fileInfo.compressedSize = fileInfo.tempCompressedSize
          delete fileInfo.tempCompressedSize

        # Release the event listener again
        @webWorker.removeEventListener 'message', responseHandler, false

        # Determine when we finished constructing the new blob
        end = Date.now()

        # Log
        $log.log(
          "#{fileInfo.name} (#{chunk + 1}/#{
            fileInfo.totalChunks
          }): Compression (#{end - start}ms, original #{
            uncompressedBlob.size
          }, compressed #{compressedBlob.size}, ratio #{((100 *
            compressedBlob.size) /
            uncompressedBlob.size).toFixed 2}%"
        )

        # Resolve the operation with the compressed blob
        dfd.resolve compressedBlob

    @webWorker.addEventListener 'message', responseHandler

    # Load the file
    fr = new FileReader()

    # Error handlers
    fr.onabort = fr.onerror = ->
      $log.error(
        "Unable to read file '#{fileInfo.name}': #{fr.error.message} (#{
          fr.error.name
        })"
      )
      dfd.reject()

    # Success flow
    fr.onload = =>
      # Post message, so the worker can start processing
      sendData =
        id: fileInfo.id
        fileName: fileInfo.name
        chunk: chunk
        totalChunks: fileInfo.totalChunks
        compressionLevel: fileInfo.compressionLevel
        uncompressedData: fr.result
        isEncrypted: fileInfo.isEncrypted
      @webWorker.postMessage sendData, [sendData.uncompressedData]

    # Read the data and return
    fr.readAsArrayBuffer uncompressedBlob
    dfd.promise

export default Compression
